import React, { useEffect, useMemo } from "react";
import Icon from "../../Icon";
import _Switch from "../../generic/_Switch";
import InputForm from "./InputForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../Context/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { ErrorMessage, SuccessMessage } from "../../../utils/toastify";
import SendCode from "./SendCode";
import _Button from "../../generic/_Button";
import { errorLabels } from "../../../utils/helpers";
import { httpClient } from "../../../utils/HttpClient";
import { ApiEndpoint } from "../../../utils/ApiEndpoint";
import ModalConfirm2FA from "./ModalConfirm";

const Enable2FA = () => {
  const auth = useAuth();
  const user = auth.getSessionData();
  const [showDisableModal, setShowDisableModal] = React.useState(false);

  const validationSchema = useMemo(() => {
    return Yup.object({
      enable2fa: Yup.boolean(),
      phone: Yup.string()
        .required("El teléfono es requerido")
        .matches(/^(?:52(?:1)?\d{10}|\d{10})$/, "Teléfono inválido"),
    });
  }, []);

  const formik = useFormik({
    initialValues: {
      code: "",
      uuid: "",
      enable2fa: user?.b_2faenabled || false,
      phone: user?.vc_phone || "",
      sendToVerify: false,
      edit_phone: false,
      step: user?.b_2faenabled ? "VERIFIED" : "INIT",
      attempts: 0,
      resetCode: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      sendCode(values);
    },
  });

  const { mutate: sendCode, isPending: isLoadingSendCode } = useMutation({
    mutationKey: ["sendCode"],
    mutationFn: async () => {
      const response = await httpClient(
        "POST",
        `${ApiEndpoint}users/send2faCode`,
        {
          phone: formik.values.phone,
          uid_user: user.uid_user,
        },
        user.token,
      );

      const res = await response.data;
      return res;
    },
    onSuccess: ({ body }) => {
      if (!body || body?.errors) {
        ErrorMessage("Error al enviar el código de verificación");
        return;
      }

      if (!body?.uuid) {
        ErrorMessage("Error al enviar el código de verificación");
        return;
      }

      formik.setFieldValue("uuid", body.uuid);
      formik.setFieldValue("step", "SENT");
      SuccessMessage("Se envió el código de verificación");
    },
    onError: () => {
      ErrorMessage("Error al enviar el código de verificación");
    },
  });

  const validate = async () => {
    try {
      const isValid = await formik.validateForm();
      const errors = errorLabels(isValid);

      if (Object.keys(isValid).length !== 0) {
        throw new Error(`Campos inválidos: ${errors}`);
      }
      await formik.submitForm();
    } catch (error) {
      ErrorMessage(error.message);
    }
  };

  useEffect(() => {
    if (formik.values.attempts >= 3) {
      formik.resetForm();
      formik.setFieldValue("step", "INIT");
      formik.setFieldValue("enable2fa", false);
    }
  }, [formik.values.attempts]);

  const { mutate: disable2FA, isPending: isLoadingDisable } = useMutation({
    mutationKey: ["disable2FA"],
    mutationFn: async () => {
      const response = await httpClient(
        "POST",
        `${ApiEndpoint}users/disable2fa`,
        {
          uiduser: user.uid_user,
        },
        user.token,
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.error) {
        ErrorMessage("Error al desactivar 2FA");
        return;
      }

      auth.updateUserData({
        b_2faenabled: false,
      });
      formik.setFieldValue("enable2fa", false);
      formik.setFieldValue("step", "INIT");
      SuccessMessage("2FA desactivado correctamente");
      setShowDisableModal(false);
    },
    onError: (error) => {
      console.log(error);
      formik.setFieldValue("enable2fa", true);
      ErrorMessage("Error al desactivar 2FA");
      setShowDisableModal(false);
    },
  });

  const { mutate: verifyAndEnable2FA, isPending: isLoadingVerify } =
    useMutation({
      mutationKey: ["verifyAndEnable2FA"],
      mutationFn: async (code) => {
        const response = await httpClient(
          "POST",
          `${ApiEndpoint}users/verify2faCode`,
          {
            code: code,
            uuid: formik.values.uuid,
            uid_user: user.uid_user,
          },
          user.token,
        );
        return response.data;
      },
      onSuccess: (data) => {
        if (data.error || !data.success) {
          formik.setFieldValue("attempts", formik.values.attempts + 1);
          ErrorMessage("Código de verificación incorrecto");
          return;
        }

        auth.updateUserData({
          b_2faenabled: true,
          vc_phone: formik.values.phone,
        });
        formik.setFieldValue("enable2fa", true);
        formik.setFieldValue("step", "VERIFIED");
        SuccessMessage("2FA activado correctamente");
      },
      onError: () => {
        formik.setFieldValue("attempts", formik.values.attempts + 1);
        ErrorMessage("Error al verificar el código");
      },
    });

  const handleSwitchChange = (newValue) => {
    if (!newValue && formik.values.step === "VERIFIED") {
      setShowDisableModal(true);
    }

    if (newValue && formik.values.step === "INIT") {
      formik.setFieldValue("enable2fa", true);
    }

    if (!newValue && formik.values.step === "INIT") {
      formik.setFieldValue("enable2fa", false);
    }

    if (!newValue && formik.values.step === "SENT") {
      formik.setValues({
        ...formik.values,
        enable2fa: false,
        step: "INIT",
        code: "",
        uuid: "",
        attempts: 0,
      });
    }
  };

  const renderStep = () => {
    switch (formik.values.step) {
      case "INIT":
        return (
          <>
            <InputForm formik={formik} isLoading={isLoadingSendCode} />
            <_Button
              style="primary"
              title="Enviar código"
              className="w-full"
              onClick={validate}
              loading={isLoadingSendCode}
              disabled={isLoadingSendCode}
            />
          </>
        );
      case "SENT":
        return (
          <SendCode
            formik={formik}
            onVerify={verifyAndEnable2FA}
            onResend={sendCode}
            isLoading={isLoadingVerify}
          />
        );

      case "VERIFIED":
        return (
          <>
            <div className="rounded-lg px-4 py-2 bg-green-100">
              <div className="flex items-center justify-between">
                <div className="text-green-700 font-medium">
                  Teléfono verificado: {formik.values.phone}
                </div>
                <Icon icon="check_circle" className="text-green-700" />
              </div>
            </div>
            <_Button
              title="Cambiar teléfono"
              icon="edit"
              style="outline"
              className="w-full my-2"
              onClick={() => {
                formik.setFieldValue("step", "INIT");
                formik.setFieldValue("enable2fa", true);
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="rounded-lg px-4 pt-2 pb-4 my-2 space-y-2 bg-white w-[800px]">
      <div className="flex justify-between gap-2 items-center my-2">
        <div className="flex justify-start gap-2">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gray-200">
            <Icon icon="security" />
          </div>
          <div className="flex flex-col">
            <div className="text-gray-800 font-semibold text-xl">
              Autenticación de dos factores
            </div>
            <span className="text-[12px] text-gray-500">
              Añade una capa extra de seguridad a tu cuenta
            </span>
          </div>
        </div>
      </div>

      <div className="border rounded-lg px-4 py-2 bg-gray-100">
        <div className="flex items-center justify-between gap-2">
          <div className="w-[100px] animation ease-in-out">
            {formik.values.enable2fa ? "Activado" : "Desactivado"}
          </div>
          <_Switch
            checked={formik.values.enable2fa}
            onChange={() => handleSwitchChange(!formik.values.enable2fa)}
            disabled={isLoadingDisable || isLoadingVerify || isLoadingSendCode}
          />
        </div>
      </div>

      {formik.values.enable2fa &&
        (formik.values.step === "INIT" ||
          formik.values.step === "SENT" ||
          formik.values.step === "VERIFIED") &&
        renderStep()}

      <ModalConfirm2FA
        title="Desactivar Autenticación de Dos Factores"
        description="¿Estás seguro de que quieres desactivar la autenticación de dos factores?. El cambio se reflejarán en el proximo inicio de sesión."
        visible={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onAction={() => {
          disable2FA();
        }}
        isLoading={isLoadingDisable}
      />
    </div>
  );
};

export default Enable2FA;
